import {
  createEmptyMemoryEnvelope,
  NINA_EVIDENCE_STATUSES,
  NINA_MEMORY_CATEGORIES,
  NINA_MEMORY_SCHEMA_VERSION,
  NINA_MEMORY_STORAGE_KEY,
  NINA_RECORD_STATUSES,
  NINA_RISK_LEVELS,
} from "./NinaMemorySchema";
import {
  areDuplicateMemoryRecords,
  createMemoryFingerprint,
  hasSecretLikeFields,
  normalizeMemoryRecord,
  validateMemoryRecord,
} from "./NinaMemoryValidation";

const resultError = (code, message, details = null) => ({
  ok: false,
  error: { code, message, details },
});

const createId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `nina-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const isIsoTimestamp = (value) =>
  typeof value === "string" &&
  Number.isFinite(Date.parse(value)) &&
  new Date(value).toISOString() === value;

const PERSISTED_RECORD_KEYS = new Set([
  "schemaVersion",
  "timestamp",
  "symbol",
  "category",
  "source",
  "sourceRecordId",
  "evidenceStatus",
  "confidence",
  "riskLevel",
  "title",
  "details",
  "outcome",
  "lesson",
  "tags",
  "userApproved",
  "id",
  "createdAt",
  "updatedAt",
  "archivedAt",
  "status",
  "dedupeKey",
]);

const REQUIRED_PERSISTED_KEYS = [
  "schemaVersion",
  "timestamp",
  "symbol",
  "category",
  "source",
  "evidenceStatus",
  "riskLevel",
  "title",
  "details",
  "outcome",
  "lesson",
  "tags",
  "userApproved",
  "id",
  "createdAt",
  "updatedAt",
  "archivedAt",
  "status",
  "dedupeKey",
];

const validateStoredRecord = (record, index) => {
  if (hasSecretLikeFields(record)) {
    return resultError(
      "UNSAFE_STORED_RECORD",
      `Memory record ${index + 1} contains credential- or secret-like data. The stored data was left unchanged.`
    );
  }

  const errors = [];
  if (!record || typeof record !== "object" || Array.isArray(record)) {
    errors.push("Record must be an object.");
  } else {
    const unexpectedKeys = Object.keys(record).filter(
      (key) => !PERSISTED_RECORD_KEYS.has(key)
    );
    const missingKeys = REQUIRED_PERSISTED_KEYS.filter(
      (key) => !Object.prototype.hasOwnProperty.call(record, key)
    );

    if (unexpectedKeys.length) errors.push("Unexpected persisted fields are not allowed.");
    if (missingKeys.length) errors.push("Required persisted fields are missing.");
    if (typeof record.id !== "string" || !record.id.trim()) errors.push("Invalid id.");
    if (
      typeof record.schemaVersion !== "number" ||
      record.schemaVersion !== NINA_MEMORY_SCHEMA_VERSION
    ) {
      errors.push("Invalid record schema version.");
    }
    if (!isIsoTimestamp(record.createdAt)) errors.push("Invalid createdAt.");
    if (!isIsoTimestamp(record.updatedAt)) errors.push("Invalid updatedAt.");
    if (!isIsoTimestamp(record.timestamp)) errors.push("Invalid timestamp.");
    if (!NINA_RECORD_STATUSES.includes(record.status)) errors.push("Invalid status.");
    if (
      record.archivedAt !== null &&
      !isIsoTimestamp(record.archivedAt)
    ) {
      errors.push("Invalid archivedAt.");
    }
    if (record.status === "archived" && !isIsoTimestamp(record.archivedAt)) {
      errors.push("Archived records require archivedAt.");
    }
    if (record.status === "active" && record.archivedAt !== null) {
      errors.push("Active records require archivedAt to be null.");
    }
    if (typeof record.symbol !== "string") {
      errors.push("Invalid symbol type.");
    } else if (
      record.symbol &&
      !/^[A-Z][A-Z0-9.-]{0,11}$/.test(record.symbol)
    ) {
      errors.push("Invalid symbol.");
    }
    if (!NINA_MEMORY_CATEGORIES.includes(record.category)) errors.push("Invalid category.");
    if (typeof record.source !== "string" || !record.source.trim()) errors.push("Invalid source.");
    if (
      record.sourceRecordId !== null &&
      record.sourceRecordId !== undefined &&
      typeof record.sourceRecordId !== "string"
    ) {
      errors.push("Invalid sourceRecordId.");
    }
    if (!NINA_EVIDENCE_STATUSES.includes(record.evidenceStatus)) {
      errors.push("Invalid evidence status.");
    }
    if (
      record.confidence !== null &&
      record.confidence !== undefined &&
      (typeof record.confidence !== "number" ||
        !Number.isFinite(record.confidence) ||
        record.confidence < 0 ||
        record.confidence > 100)
    ) {
      errors.push("Invalid confidence.");
    }
    if (!NINA_RISK_LEVELS.includes(record.riskLevel)) errors.push("Invalid risk level.");
    for (const field of ["title", "details", "outcome", "lesson"]) {
      if (typeof record[field] !== "string") errors.push(`Invalid ${field}.`);
    }
    if (
      ["title", "details", "outcome", "lesson"].every(
        (field) => typeof record[field] !== "string" || !record[field].trim()
      )
    ) {
      errors.push("At least one meaningful text field is required.");
    }
    if (
      !Array.isArray(record.tags) ||
      record.tags.some((tag) => typeof tag !== "string")
    ) {
      errors.push("Invalid tags.");
    }
    if (typeof record.userApproved !== "boolean") errors.push("Invalid userApproved.");
    if (record.category === "user-approved-rule" && record.userApproved !== true) {
      errors.push("User-approved rules require explicit approval.");
    }
    if (typeof record.dedupeKey !== "string" || !record.dedupeKey.trim()) {
      errors.push("Invalid dedupeKey.");
    }
  }

  if (errors.length) {
    return resultError(
      "INVALID_STORED_RECORD",
      `Memory record ${index + 1} is malformed. The stored data was left unchanged.`,
      errors
    );
  }

  return { ok: true, value: record };
};

export const createNinaMemoryStore = (
  storage = window.localStorage,
  clock = () => new Date().toISOString()
) => {
  const readEnvelope = () => {
    let raw;
    try {
      raw = storage.getItem(NINA_MEMORY_STORAGE_KEY);
    } catch (error) {
      return resultError("STORAGE_READ_FAILED", "Memory storage could not be read.", error);
    }

    if (!raw) return { ok: true, value: createEmptyMemoryEnvelope(clock()) };

    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (error) {
      return resultError(
        "CORRUPTED_STORAGE",
        "Memory data is corrupted and was left unchanged.",
        error
      );
    }

    if (parsed?.schemaVersion !== NINA_MEMORY_SCHEMA_VERSION) {
      return resultError(
        "UNSUPPORTED_SCHEMA",
        `Memory schema ${parsed?.schemaVersion ?? "unknown"} is not supported.`
      );
    }

    if (!Array.isArray(parsed.records)) {
      return resultError("INVALID_ENVELOPE", "Memory records are invalid.");
    }

    for (let index = 0; index < parsed.records.length; index += 1) {
      const validation = validateStoredRecord(parsed.records[index], index);
      if (!validation.ok) return validation;
    }

    return { ok: true, value: parsed };
  };

  const writeEnvelope = (envelope) => {
    try {
      storage.setItem(NINA_MEMORY_STORAGE_KEY, JSON.stringify(envelope));
      return { ok: true, value: envelope };
    } catch (error) {
      const quota = error?.name === "QuotaExceededError";
      return resultError(
        quota ? "STORAGE_QUOTA_EXCEEDED" : "STORAGE_WRITE_FAILED",
        quota
          ? "Browser storage is full. Export a backup before clearing space."
          : "Memory storage could not be updated.",
        error
      );
    }
  };

  const listRecords = (filters = {}) => {
    const read = readEnvelope();
    if (!read.ok) return read;

    const search = String(filters.search || "").trim().toLowerCase();
    const status = filters.status || "active";
    const filtered = read.value.records.filter((record) => {
      const matchesStatus = status === "all" || record.status === status;
      const matchesCategory = !filters.category || record.category === filters.category;
      const matchesEvidence =
        !filters.evidenceStatus || record.evidenceStatus === filters.evidenceStatus;
      const matchesRisk = !filters.riskLevel || record.riskLevel === filters.riskLevel;
      const haystack = [
        record.symbol,
        record.title,
        record.details,
        record.outcome,
        record.lesson,
        record.source,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return (
        matchesStatus &&
        matchesCategory &&
        matchesEvidence &&
        matchesRisk &&
        (!search || haystack.includes(search))
      );
    });

    return {
      ok: true,
      value: filtered.sort(
        (first, second) => Date.parse(second.timestamp) - Date.parse(first.timestamp)
      ),
    };
  };

  const getRecord = (id) => {
    const read = readEnvelope();
    if (!read.ok) return read;
    const record = read.value.records.find((item) => item.id === id);
    return record
      ? { ok: true, value: record }
      : resultError("NOT_FOUND", "Memory record was not found.");
  };

  const createRecord = (input) => {
    if (hasSecretLikeFields(input)) {
      return resultError(
        "VALIDATION_FAILED",
        "Memory record is invalid.",
        ["Credential- or secret-like fields are not allowed."]
      );
    }
    const now = clock();
    const normalized = normalizeMemoryRecord(
      { ...input, timestamp: input?.timestamp || now },
      now
    );
    const validation = validateMemoryRecord(normalized);
    if (!validation.valid) {
      return resultError("VALIDATION_FAILED", "Memory record is invalid.", validation.errors);
    }

    const read = readEnvelope();
    if (!read.ok) return read;

    const dedupeKey = createMemoryFingerprint(normalized);
    const duplicate = read.value.records.find((record) =>
      areDuplicateMemoryRecords(normalized, record)
    );
    if (duplicate) return { ok: true, value: duplicate, duplicate: true };

    const record = {
      ...normalized,
      id: createId(),
      createdAt: now,
      updatedAt: now,
      archivedAt: null,
      status: "active",
      dedupeKey,
    };
    const envelope = {
      ...read.value,
      updatedAt: now,
      records: [...read.value.records, record],
    };
    const written = writeEnvelope(envelope);
    return written.ok ? { ok: true, value: record, duplicate: false } : written;
  };

  const updateRecord = (id, changes = {}) => {
    if (hasSecretLikeFields(changes)) {
      return resultError(
        "VALIDATION_FAILED",
        "Memory record is invalid.",
        ["Credential- or secret-like fields are not allowed."]
      );
    }
    const read = readEnvelope();
    if (!read.ok) return read;
    const current = read.value.records.find((record) => record.id === id);
    if (!current) return resultError("NOT_FOUND", "Memory record was not found.");

    const protectedFields = [
      "id",
      "createdAt",
      "schemaVersion",
      "dedupeKey",
      "status",
      "archivedAt",
    ];
    const safeChanges = Object.fromEntries(
      Object.entries(changes).filter(([key]) => !protectedFields.includes(key))
    );
    const normalized = normalizeMemoryRecord(
      { ...current, ...safeChanges, timestamp: safeChanges.timestamp || current.timestamp },
      current.createdAt
    );
    const validation = validateMemoryRecord(normalized);
    if (!validation.valid) {
      return resultError("VALIDATION_FAILED", "Memory record is invalid.", validation.errors);
    }

    const duplicate = read.value.records.find(
      (record) =>
        record.id !== id && areDuplicateMemoryRecords(normalized, record)
    );
    if (duplicate) {
      return resultError(
        "DUPLICATE_RECORD",
        "This update would duplicate another memory record. No records were changed."
      );
    }

    const now = clock();
    const updated = {
      ...current,
      ...normalized,
      id: current.id,
      createdAt: current.createdAt,
      updatedAt: now,
      dedupeKey: createMemoryFingerprint(normalized),
      status: current.status,
      archivedAt: current.archivedAt,
    };
    const envelope = {
      ...read.value,
      updatedAt: now,
      records: read.value.records.map((record) => (record.id === id ? updated : record)),
    };
    const written = writeEnvelope(envelope);
    return written.ok ? { ok: true, value: updated } : written;
  };

  const setArchiveStatus = (id, archived) => {
    const read = readEnvelope();
    if (!read.ok) return read;
    const current = read.value.records.find((record) => record.id === id);
    if (!current) return resultError("NOT_FOUND", "Memory record was not found.");
    const now = clock();
    const updated = {
      ...current,
      status: archived ? "archived" : "active",
      archivedAt: archived ? now : null,
      updatedAt: now,
    };
    const envelope = {
      ...read.value,
      updatedAt: now,
      records: read.value.records.map((record) => (record.id === id ? updated : record)),
    };
    const written = writeEnvelope(envelope);
    return written.ok ? { ok: true, value: updated } : written;
  };

  const clearRecords = ({ confirmed = false } = {}) => {
    if (confirmed !== true) {
      return resultError("CONFIRMATION_REQUIRED", "Clearing memory requires confirmation.");
    }
    const read = readEnvelope();
    if (!read.ok) return read;
    const now = clock();
    return writeEnvelope({ ...read.value, updatedAt: now, records: [] });
  };

  const getSummary = () => {
    const read = readEnvelope();
    if (!read.ok) return read;
    const active = read.value.records.filter((record) => record.status === "active");
    return {
      ok: true,
      value: {
        active: active.length,
        archived: read.value.records.length - active.length,
        simulatedTrades: active.filter((record) => record.category === "simulated-trade").length,
        lessonsLearned: active.filter((record) => record.category === "lesson-learned").length,
        approvedRules: active.filter((record) => record.category === "user-approved-rule").length,
      },
    };
  };

  const exportRecords = () => {
    const read = readEnvelope();
    if (!read.ok) {
      return resultError(
        read.error.code,
        `${read.error.message} JSON export was blocked.`,
        read.error.details
      );
    }
    return { ok: true, value: JSON.stringify(read.value, null, 2) };
  };

  return {
    readEnvelope,
    createRecord,
    getRecord,
    updateRecord,
    listRecords,
    archiveRecord: (id) => setArchiveStatus(id, true),
    restoreRecord: (id) => setArchiveStatus(id, false),
    clearRecords,
    getSummary,
    exportRecords,
  };
};

const NinaMemoryStore = createNinaMemoryStore();
export default NinaMemoryStore;
